import {
  LayoutGraph,
  LayoutStageBase,
  Insets,
  HierarchicalLayout,
  LayoutKeys,
  Size,
} from '@ardoq/yfiles';
import { groupNodeInsets } from './groupNodeInsets';
import { MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING as PADDING } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { MODERNIZED_BLOCK_DIAGRAM_LEFT_INSET as LEFT_INSET } from './consts';

/**
 * A layout stage that runs a hierarchical layout, then calculates group node insets, then runs the hierarchical layout a second time, as an incremental layout.
 *
 * This allows the group headers to be sized according to the child bounds, as well as the text that needs to be displayed in the group header.
 */
class ApplyGroupInsetsLayoutStage extends LayoutStageBase {
  override applyLayoutImpl(graph: LayoutGraph): void {
    if (!(this.coreLayout instanceof HierarchicalLayout)) {
      return;
    }

    // Optimization: If there are no groups, skip the dual-pass layout and just run once.
    // The second pass only exists to apply correct group header insets.
    const groupCount = Array.from(graph.nodes).filter(node =>
      graph.isGroupNode(node)
    ).length;

    if (groupCount === 0) {
      this.coreLayout.applyLayout(graph);
      return;
    }

    // #region run initial HierarchicalLayout. ensure horizontal insets are stable between the two runs.
    graph.context.addItemConstantData(
      LayoutKeys.GROUP_NODE_PADDING_DATA_KEY,
      new Insets(0, PADDING, 0, LEFT_INSET)
    );

    this.coreLayout.applyLayout(graph);

    graph.context.remove(LayoutKeys.GROUP_NODE_PADDING_DATA_KEY);
    // #endregion

    // #region run HierarchicalLayout again incrementally with the full insets provider.
    const groupInsetsMapper = graph.createNodeDataMap<Insets>();
    const minimumSizeMapper = graph.createNodeDataMap<Size>();
    for (const node of graph.nodes) {
      if (!graph.isGroupNode(node)) {
        continue;
      }
      // calculate the final insets. the top inset is dependent on the node label and the node width from the previous stage.
      groupInsetsMapper.set(node, groupNodeInsets(graph, node));
      // ensure groups do not decrease their width during the second HierarchicalLayout pass.
      minimumSizeMapper.set(node, new Size(node.layout.width, 0));
    }

    graph.context.addItemData(
      LayoutKeys.GROUP_NODE_PADDING_DATA_KEY,
      groupInsetsMapper
    );
    graph.context.addItemData(
      LayoutKeys.MINIMUM_GROUP_NODE_SIZE_DATA_KEY,
      minimumSizeMapper
    );

    const { fromSketchMode } = this.coreLayout;
    this.coreLayout.fromSketchMode = true;
    this.coreLayout.applyLayout(graph);
    this.coreLayout.fromSketchMode = fromSketchMode;

    graph.context.remove(LayoutKeys.GROUP_NODE_PADDING_DATA_KEY);
    graph.context.remove(LayoutKeys.MINIMUM_GROUP_NODE_SIZE_DATA_KEY);
    // #endregion
  }
}
export { ApplyGroupInsetsLayoutStage };
